// The Chats page (TOM-195): the owner's conversations with Neo on one boat.
//
// GET /v1/neo/conversations?boatId= lists them newest first (specs §12.13).
// The page shows the main chat first, then the others. The main chat is the
// conversation the server keeps for the owner and boat (TOM-517, `isMain`), so
// it is left out of the others and listed once.
//
// Pure, so cloud/web's Node vitest covers it (no DOM harness).

/** One listed conversation, as the page shows it. */
export interface NeoChatItem {
  id: string;
  boatId: string | null;
  /** The owner's first message or their rename; null before either. */
  title: string | null;
  lastMessageAt: string;
  kind: 'chat' | 'watch';
  /** The owner's main chat on the boat (TOM-517). */
  isMain: boolean;
}

/** One page of the list. `next` is the cursor for the older page, or null. */
export interface NeoChatPage {
  items: NeoChatItem[];
  next: string | null;
}

/** The list request for one boat, optionally continuing from a cursor. */
export function neoChatsUrl(boatId: string, cursor?: string | null): string {
  const params = new URLSearchParams({ boatId });
  if (cursor !== undefined && cursor !== null) params.set('cursor', cursor);
  return `/v1/neo/conversations?${params.toString()}`;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toItem(raw: unknown): NeoChatItem | null {
  if (!isObject(raw)) return null;
  const { id, boatId, title, lastMessageAt, kind, isMain } = raw;
  if (typeof id !== 'string' || id.length === 0) return null;
  if (boatId !== null && typeof boatId !== 'string') return null;
  if (title !== null && typeof title !== 'string') return null;
  if (typeof lastMessageAt !== 'string' || Number.isNaN(Date.parse(lastMessageAt))) return null;
  return {
    id,
    boatId,
    title,
    lastMessageAt,
    kind: kind === 'watch' ? 'watch' : 'chat',
    isMain: isMain === true,
  };
}

/**
 * The route's 200 body as a page, or null when it is not one. A single
 * malformed item is dropped.
 */
export function parseNeoChatPage(body: unknown): NeoChatPage | null {
  if (!isObject(body) || !Array.isArray(body.items)) return null;
  const items: NeoChatItem[] = [];
  for (const raw of body.items) {
    const item = toItem(raw);
    if (item !== null) items.push(item);
  }
  const next = typeof body.next === 'string' && body.next.length > 0 ? body.next : null;
  return { items, next };
}

/** The main chat's conversation among the listed ones; null while it holds nothing. */
export function mainNeoChatId(items: readonly NeoChatItem[]): string | null {
  return items.find((item) => item.isMain)?.id ?? null;
}

/**
 * The chats listed under the main chat: every item but the main chat's own
 * conversation, without repeats across pages, in the order received.
 */
export function otherNeoChats(
  items: readonly NeoChatItem[],
  mainConversationId: string | null
): NeoChatItem[] {
  const seen = new Set<string>();
  const out: NeoChatItem[] = [];
  for (const item of items) {
    if (item.id === mainConversationId || seen.has(item.id)) continue;
    seen.add(item.id);
    out.push(item);
  }
  return out;
}

const TITLE_MAX = 80;

/**
 * The row's title. The server sends the owner's first message in full (up to
 * 8000 characters), so it is put on one line and cut at a word near 80
 * characters. With no title: "Watch" for the Watch thread, else "New chat".
 */
export function neoChatTitle(item: Pick<NeoChatItem, 'title' | 'kind'>): string {
  const flat = (item.title ?? '').replace(/\s+/g, ' ').trim();
  if (flat.length === 0) return item.kind === 'watch' ? 'Watch' : 'New chat';
  if (flat.length <= TITLE_MAX) return flat;
  const cut = flat.slice(0, TITLE_MAX);
  const space = cut.lastIndexOf(' ');
  return `${(space > TITLE_MAX / 2 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/**
 * When the chat last had a message, in local time: the time today
 * ("9:41 AM"), "Yesterday", the weekday within the last week, then the date
 * ("Sep 12"), with the year when it is not this year.
 */
export function neoChatWhen(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const days = Math.round((startOfDay(now) - startOfDay(at)) / DAY_MS);
  if (days <= 0) {
    return at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
  if (days === 1) return 'Yesterday';
  if (days < 7) return at.toLocaleDateString('en-US', { weekday: 'long' });
  if (at.getFullYear() === now.getFullYear()) {
    return at.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  return at.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
